import { config } from "../config";
import {
  isResolvingBandCode,
  risingToResolvingCode,
} from "../shared/eventCodeRange";

export type CounterpartEventRef = {
  global_event_id: number;
  event_code: number;
  event_type: string;
  event_contract_name: string;
  attributes?: {
    creator_resolving_linked_rising_global_id?: number;
  } | null;
};

// Keep in sync with backend "visually empty contract" cleanup:
// - trim
// - remove common invisible chars (NBSP, zero-width, BOM, soft hyphen, ...)
// - collapse whitespace runs
const INVISIBLE_CONTRACT_CHARS_RE =
  /[\u00a0\u200b\u200c\u200d\ufeff\u202f\u00ad]/g;

function normalizeForMatch(value: unknown): string {
  const s = String(value ?? "");
  const cleaned = s.replace(INVISIBLE_CONTRACT_CHARS_RE, " ");
  // Replace tabs/newlines with spaces and collapse runs
  return cleaned.replace(/[\t\r\n]+/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Latest global_event_id for exact code + type + contract (list API, latest per group).
 */
export async function fetchLatestGlobalIdForCodeTypeContract(
  callApi: (url: string, method: string, body?: object) => Promise<any>,
  eventCode: number,
  eventType: string,
  eventContractName: string
): Promise<number | null> {
  const params = new URLSearchParams({
    event_code_exact: String(eventCode),
    latest: "true",
    page_size: "25",
  });
  if (eventType) {
    params.append("event_type", eventType);
  }
  const res = await callApi(
    `${config.apiUrl}/api/events/?${params.toString()}`,
    "GET"
  );
  const rows = res?.results ?? [];
  const contract = normalizeForMatch(eventContractName);
  const typ = normalizeForMatch(eventType);
  const fallbackTypeMatches: number[] = [];
  for (const r of rows) {
    if (normalizeForMatch(r.event_type) !== typ) continue;
    if (Number(r.event_code) !== Number(eventCode)) continue;
    const id = Number(r.global_event_id);
    if (!Number.isFinite(id) || id <= 0) continue;

    if (normalizeForMatch(r.event_contract_name) === contract) {
      return id;
    }
    fallbackTypeMatches.push(id);
  }

  // If contract didn't match but there is exactly one candidate for the code+type,
  // return it to avoid hiding a valid counterpart due to contract-name formatting drift.
  if (fallbackTypeMatches.length === 1) {
    return fallbackTypeMatches[0];
  }
  return null;
}

/**
 * Resolving → rising id (linked creator id or code − 10000).
 * Rising (non-resolving band) → resolving id (code + 10000).
 */
export async function resolveCounterpartGlobalIds(
  callApi: (url: string, method: string, body?: object) => Promise<any>,
  event: CounterpartEventRef
): Promise<{ rising: number | null; resolving: number | null }> {
  const code = Number(event.event_code);
  const type = normalizeForMatch(event.event_type);
  const contract = normalizeForMatch(event.event_contract_name);

  let rising: number | null = null;
  let resolving: number | null = null;

  if (isResolvingBandCode(code)) {
    const linked = event.attributes?.creator_resolving_linked_rising_global_id;
    if (typeof linked === "number" && linked > 0) {
      rising = linked;
    } else {
      const risingCode = code - 10000;
      if (risingCode >= 0) {
        rising = await fetchLatestGlobalIdForCodeTypeContract(
          callApi,
          risingCode,
          type,
          contract
        );
      }
    }
  } else {
    const resolvingCode = risingToResolvingCode(code);
    resolving = await fetchLatestGlobalIdForCodeTypeContract(
      callApi,
      resolvingCode,
      type,
      contract
    );
  }

  return { rising, resolving };
}
